import { FC } from 'react';
import {
  TvInternetLinkoutOffer,
  TvInternetOffer,
} from '@updater/consumer-graph';
import { ProviderConfig } from './ProviderConfig';
import { Address } from './Address';
import { MicrositeTheme } from './MicrositeTheme';
import { Qual } from './Qual';

export enum CheckoutType {
  LEAD_MODE = 'lead_mode',
  ASYNC = 'async',
  CALL_MODE = 'call_mode',
  // Some providers won't let us sell their products on their marketing microsites.
  // They want us to capture the traffic, but if a user is eligible, they
  // want us to redirect automatically to *their* offers page immediately.
  // Spectrum is the only provider that currently does this
  PROVIDER_REDIRECT = 'provider_redirect',
}

export enum ShopType {
  // Single provider means we're only selling 1 provider on this site.
  // These microsites will have 3 different s12y queries:
  // (1) one for the main provider alone
  // (2) one for other providers that are the same type as the main provider
  //   (if the main provider is an MSO, the other providers will be the other MSOs)
  // (3) one for *all* providers
  SINGLE_PROVIDER = 'single',
  // Multi provider means more than one provider is sent to s12y
  MULTI_PROVIDER = 'multi',
  // Synthetic is anything in between (> 2 but < all)
  SYNTHETIC = 'synthetic',
}

interface CustomComponents {
  // Whether we want a custom confirmation page
  Confirmation?: FC;
  // Whether we want a custom Lead Error page
  LeadError?: FC;
  // Whether we want a custom loading component
  PageLoading?: FC<any>;
}

export interface Urls {
  development?: string;
  staging?: string;
  qa?: string;
  production?: string;
}

export interface MicrositeConfig {
  // `name` is a value that should match the typescript class name.
  // it's used only in logging
  name: string;

  // optional: display name for the microsite partner, used by landing page
  displayName?: string;

  // `micrositeCode` is the value passed from the WordPress pages.
  // Provider App checks this value when the app initializes to determine
  // what microsite config to load!
  micrositeCode: string;

  // Whether this microsite is a single / multi / etc.
  shopType: ShopType;

  // TODO: domain is not used by any app code and should be removed
  domain: string;

  // Various edge cases will send the user to a dead end if they're not
  // eligible or for other reasons.  We offer to send them back to the
  // homepage of the site if this happens.  Should be a complete URL.
  homepage: string;

  // Whatever you want in the <title> tag
  title: string;

  // Some microsites have special pages to sell internet plans if the
  // user enters a commercial address (Xfinity is one of these).  If you
  // provide a value here, there's special logic to pop up a modal that
  // offers to sell you a commercial plan.  This should a complete URL.
  commercialHomepage?: string;

  // Phone Numbers are hard.  This is the CallRail swap number.  CallRail
  // is a service we use that creates real phone numbers for customers to call
  // on the fly, and it's used for call center tracking and attributions.
  // Each provider generally has a single "swap number" that we use.  CallRail's
  // javascript will look for the swap number anywhere it appears, and dynamically
  // replace it with the custom number for tracking and attribution purposes.
  // See the CallRailPhone component for details on how this works.
  // If you need to add or change a phone number for a microsite, go here:
  //    https://app.callrail.com/settings/a/501092119/routing?company_id=947929203
  fallbackPhoneNumber: string;

  // Some microsites have different swap numbers for the Lead Mode confirmation page.
  // Leave this blank if you can't find one in CallRail's dashboard.
  // Note that s12y will also return a confirmation number with the createLead mutation.
  // That phone number should take precedence, with this one acting as a fallback
  fallbackLeadModePhoneNumber: string;

  // We have two kinds of checkout:
  // (1) Lead Mode -- this is where a user enters their name and contact info,
  //   and they get a confirmation number and instructions to call to complete
  //   their order
  // (2) Async -- this is where they can checkout async through a buyflow
  // (3) Call -- this is where they are required to call in to order
  // Indicate the default checkout type here.
  defaultCheckoutType: CheckoutType;

  // Optional: Specify the checkout type when the offers at the address have an active service
  activeServiceCheckoutType?: CheckoutType;

  // This is the default visual theme that is loaded with this microsite.
  // Note that there is business logic to "swap" themes, so the visual theming
  // is kept completely separate from the microsite configuration.
  defaultTheme: MicrositeTheme;

  // Do we need a DC session ID for this microsite for any reason?  Since s12y
  // returns the Falcon TID, we don't need a DC session if the user isn't
  // leaving provider app.  Currently the only user journies requiring a DC
  // session ID are:
  // (1) xfinity async guest-buyflow, which reads off DC
  // Setting this to `true` will trigger the polling behavior to get a DC
  // session ID for the primaryData request.
  needsDcSession: boolean;

  // Are we cross-selling to updater.com/internet?
  updaterCrossSellActive: boolean;

  iFrameConfig?: {
    // Which pages may embed us, enforced by WhitelistGuard in production only.
    // Entries are bare host patterns — `hinthome.com` matches that host exactly,
    // `*.securecafe.com` matches any subdomain but not the apex.
    trustedHosts?: { production?: string[] };
    // whether to hide the header and footer (for iframe contexts)
    hideHeaderAndFooter?: boolean;
  };

  // Optional: Google Tag Manager code for this microsite
  // This is usually sent in the qual from the WordPress pages
  // If it does come in the qual, this value will be IGNORED
  // If it does NOT come in the qual, this value will be used
  gtmCode?: string;

  // Optional: suppress standalone NormalizedAddress check
  suppressNormalizedAddressCheck?: boolean;

  // This is value used by the data team; it's sent as a serviceability parameter.
  // Contact the backend and data teams to define what this should be.
  source: string;

  // If the user enters an address that doesn't get any offers back, the
  // product behavior is to re-prompt them to validate their address in
  // a modal.  This is because users sometimes enter weird stuff in their
  // address that somehow passes the normalization on the WordPress pages.
  // We re-prompt them to validate their address this many times before
  // showing them a "dead end" modal to call our call center.
  noOffersRetry: number;

  // Function that determines the checkout flow based on the offer
  // object from serviceability
  // Note: config/sharedFunctions has a few common implementations
  // you can use here, such as `allLeadMode` and `asyncEligible`
  getCheckoutType: (
    offer: TvInternetOffer,
    enabledProviders?: Set<string>
  ) => CheckoutType;

  // any component overrides needed for this microsite/theme
  customComponents?: CustomComponents;

  /**
   * Enable address-entry landing page
   * Note: requires a displayName for rendering copy, defaults to 'Updater' if not provided
   */
  enableMicrositeLanding?: boolean;
  /**
   * When true, commercial/business addresses are allowed through without showing the
   * CommercialAddressModal warning. Intended for embedded microsites (e.g. Yardi) where
   * business addresses are expected and should not block offers.
   */
  allowCommercialAddress?: boolean;

  /**
   * When set, generates a qual on load for users arriving without one.
   */
  qualOnLoad?: {
    /** Hostname to match against window.location.hostname. Qual is generated only when none is present in the URL. */
    hostname: string;
    promoId?: string;
  };

  /**
   * Center the quiz container. Defaults to false (left aligned).
   */
  centerQuizContainer?: boolean;

  // Per-environment moving app buyflow URLs, for static microsite configs
  // whose buyflow domain differs per environment.
  movingAppBuyflowUrls?: Urls;

  // Base moving app URL for remote-config microsites. One value covers every
  // environment, since the config fetch is already environment-scoped.
  // Consumers append their own path. Takes precedence over the map above.
  appUrl?: string;
}

export interface CrossSellInput {
  providerConfig?: ProviderConfig;
  serviceabilityOffer?: TvInternetOffer;
  // Offers as they arrived from s12y. Linkout offers among them carry a
  // prebuilt ordering url, which the provider redirect prefers over any url
  // this app assembles by hand.
  serviceabilityOffers?: (TvInternetOffer | TvInternetLinkoutOffer)[];
  baseUrl?: string;
  dcFwsTid?: string;
  dcSessionId?: string;
  uuid?: string;
  address?: Address;
  qual?: Qual;
}

export enum CrossSellType {
  CROSS_SELL_SHOP = 'cross_sell_shop',
  CROSS_SELL_RESKIN = 'cross_sell_reskin',
  CROSS_SELL_REDIRECT = 'cross_sell_redirect',
}

export interface CrossSellAction {
  crossSellType: CrossSellType;
  reskinProviderConfig?: ProviderConfig;
  crossSellUrl?: string;
}

// SINGLE PROVIDER MICROSITES
// These are microsites where we sell offers from 1 provider only
// by default.  These sites have special cross-sell behavior based
// on this configuration.  They are generally matched 1-to-1 with
// a theme.
export interface SingleProviderConfig extends MicrositeConfig {
  // The "main" provider for this microsite.
  mainProvider: ProviderConfig;

  // A function to get the cross-sell URL based on given input.
  getCrossSellAction: (data: CrossSellInput) => CrossSellAction;

  // This is an ORDERED LIST of cross-sell providers for this
  // microsite.  The general rules for cross-selling are:
  // (a) if the mainProvider is an MSO, we can cross-sell other MSOs
  // (b) if the mainProvider is a Telco, we can cross-sell other Telcos
  // In order to maximize conversion rates, we display only a single
  // provider in the cross-sell experience based on business metrics.
  // If there are offers available from multiple providers here, the
  // app will scan the available list from index 0 to n and choose the
  // cross-sell provider with the LOWEST index.
  // There are funky rules with cross-selling, so you must explicitly
  // and exhaustively define a cross-sell list for every microsite.
  crossSellProviders?: ProviderConfig[];

  // This is a list of providers that we can cross-sell on Updater.com/internet,
  // but *not* direct cross-sell.  The business rules are a bit confusing,
  // but this list will generally be the *inverse* of the crossSellProviders
  // above.  Meaning, if crossSellProviders above are a list of MSOs, this will
  // be a list of Telcos and vice versa.
  updaterCrossSellProviders?: ProviderConfig[];

  // A list of seconary providers that we consider "primary"
  // for shops with complex synthetic rules
  syntheticProviders?: ProviderConfig[];

  // If the checkout type is PROVIDER_REDIRECT, we must supply a function
  // to generate the provider redirect URL.  This is currently the same
  // function as the cross-sell URLs, but these are different product
  // use cases so we should really keep them separate.
  // Returns undefined when no usable url exists, which holds the redirect and
  // lets the user fall through to /plans rather than stranding them.
  getProviderRedirectUrl?: (data: CrossSellInput) => string | undefined;

  // Growthbook flag gating the PROVIDER_REDIRECT behavior. Absent means the
  // redirect is always on. Present and off — or Growthbook unreachable — fails
  // closed to the normal /plans experience.
  providerRedirectFeatureFlag?: string;

  // Opt-in: redirect when the primary query settles with no offers at the
  // address, instead of the retry and not-serviceable modals. Still needs
  // getProviderRedirectUrl to return a url, otherwise those modals take over.
  redirectWhenUnavailable?: boolean;
}

// MULTIPLE PROVIDER MICROSITES
// These are microsites where we can sell any number of providers on
// the same page, just like we do in Consumer App.  These microsites
// have *no* cross-sell functionality because cross-selling as a concept
// makes no sense if we can sell everything on the same page.
// For these sites, all you need to do is define a list of providers
// that this site should query serviceability for and display.  Generally
// this is *every* provider, though the sales and business teams will
// want to tweak this based on individual rules from certain providers, so
// you must exhaustively list every provider
export interface MultiProviderConfig extends MicrositeConfig {
  providers: ProviderConfig[];
}

export type MicrositeConfigType = SingleProviderConfig | MultiProviderConfig;
